// 1.2.17: 收藏夹页面 - 优化性能，使用前端缓存实现快速加载
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
// 2.0.0: 移除 ChefHat，加载态改用骨架屏
import { Star, ArrowLeft, Loader2 } from 'lucide-react';
import { RestaurantCard } from '../../../shared/components/RestaurantCard';
import { getFavorites, removeFavorite } from '../../../shared/services/favoriteService';
import { getCurrentUser } from '../../../shared/services/authService';
import type { Restaurant } from '../../search/types/restaurant';

export function FavoritesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});

  // Check authentication and redirect if not logged in
  useEffect(() => {
    const checkAuth = async () => {
      const user = await getCurrentUser();
      if (!user) {
        // Redirect to home page if not authenticated
        navigate('/');
        return;
      }
      
      // Load favorites
      await loadFavorites();
    };

    checkAuth();
  }, [navigate]);

  // 1.2.17: 优化加载逻辑 - 先显示缓存数据，后台同步最新数据
  const loadFavorites = async () => {
    setIsLoading(true);
    try {
      // 1.2.17: getFavorites() 会先返回缓存数据（如果存在且有效），然后在后台同步最新数据
      // 这样可以实现页面立即显示，后台静默更新的效果
      const favoriteList = await getFavorites();
      setFavorites(favoriteList);
      
      // 1.2.17: 直接从收藏列表设置状态为 true，避免 N+1 查询
      // 这些餐厅已经在收藏列表中，状态必然是 true，不需要逐个查询
      const status: Record<string, boolean> = {};
      favoriteList.forEach((restaurant) => {
        status[restaurant.id] = true;
      });
      setFavoriteStatus(status);
      
      // 1.2.17: 后台同步最新数据（getFavorites 内部已实现，这里确保数据更新）
      // 如果缓存数据已显示，后台同步完成后会自动更新
      // 为了确保数据最新，可以再次调用（但 getFavorites 内部会处理缓存逻辑）
      setTimeout(async () => {
        try {
          const latestFavorites = await getFavorites();
          // 只有在数据发生变化时才更新（避免不必要的重新渲染）
          if (JSON.stringify(latestFavorites) !== JSON.stringify(favoriteList)) {
            setFavorites(latestFavorites);
            const latestStatus: Record<string, boolean> = {};
            latestFavorites.forEach((restaurant) => {
              latestStatus[restaurant.id] = true;
            });
            setFavoriteStatus(latestStatus);
          }
        } catch (error) {
          // 后台同步失败不影响已显示的数据
          if (import.meta.env.DEV) {
            console.error('Error syncing favorites in background:', error);
          }
        }
      }, 100); // 延迟100ms执行，确保首次加载已完成
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading favorites:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFavoriteClick = async (restaurant: Restaurant) => {
    // Remove from favorites
    const result = await removeFavorite(restaurant.id);
    if (result.success) {
      // Update local state
      setFavorites(prev => prev.filter(r => r.id !== restaurant.id));
      setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: false }));
    }
  };

  // 2.0.0: 加载态改为骨架屏（3列 skeleton 卡片）
  if (isLoading) {
    return (
      <div className="h-full bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* 2.0.0: 骨架屏标题区域 */}
          <div className="mb-8">
            <div className="h-5 w-16 bg-gray-200 dark:bg-gray-800 rounded mb-4 animate-pulse" />
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-800 rounded mb-2 animate-pulse" />
            <div className="h-4 w-32 bg-gray-100 dark:bg-gray-800/50 rounded animate-pulse" />
          </div>
          {/* 2.0.0: 骨架屏卡片网格 — 3列布局 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200 dark:bg-gray-800" />
                <div className="p-4 space-y-3">
                  <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-800 rounded" />
                  <div className="h-4 w-1/2 bg-gray-100 dark:bg-gray-800/50 rounded" />
                  <div className="h-4 w-2/3 bg-gray-100 dark:bg-gray-800/50 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    // 2.0.0: 页面主容器 — 嵌入主布局，保留 sidebar + header
    <div className="h-full bg-white dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header — 截图风格: 小返回按钮 + 大标题 */}
        <div className="mb-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm
                       border border-gray-300 dark:border-gray-600 rounded-lg
                       text-gray-600 dark:text-gray-400
                       hover:text-orange-600 dark:hover:text-orange-400
                       hover:border-orange-400 dark:hover:border-orange-500
                       transition-colors duration-200 mb-4"
              aria-label={t('back', '返回')}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('back', 'Back')}</span>
            </button>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {t('favoritesGalleryTitle', 'Saved Culinary Destinations Gallery')}
            </h1>
          </motion.div>
        </div>

        {/* Favorites Grid */}
        {favorites.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
            {favorites.map((restaurant, index) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                index={index}
                isFavorite={favoriteStatus[restaurant.id] || false}
                onFavoriteClick={handleFavoriteClick}
              />
            ))}
          </div>
        ) : (
          /* 2.0.0: 空状态双主题 */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="max-w-md mx-auto">
              {/* 2.0.0: 空状态圆形背景双主题 */}
              <div className="w-24 h-24 mx-auto mb-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <Star className="w-12 h-12 text-orange-500" />
              </div>
              {/* 2.0.0: 空状态标题双主题 */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t('noFavorites', '暂无收藏的餐厅')}
              </h3>
              {/* 2.0.0: 空状态描述双主题 */}
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {t('noFavoritesDescription', '开始收藏您喜欢的餐厅吧！')}
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

